import {
  BarController,
  BarElement,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { ChartData } from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import { chartColors } from '../../util/chartjs-config';
import { TimeScale } from 'chart.js';
import 'chartjs-adapter-moment'

Chart.register(
  BarController,
  BarElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend
);

const formatValue = (value: number): string =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'SGD',
    maximumSignificantDigits: 3,
    notation: 'compact',
  }).format(value);

const BarChart = ({ data, width, height }: PropsType) => {
  const [chart, setChart] = useState<Chart | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const {
    textColor,
    gridColor,
    tooltipBodyColor,
    tooltipBgColor,
    tooltipBorderColor,
  } = chartColors;

  useEffect(() => {
    const ctx = canvas.current;
    if (!ctx) return;

    const newChart = new Chart(ctx, {
      type: 'bar',
      data: data,
      options: {
        layout: {
          padding: {
            top: 12,
            bottom: 16,
            left: 20,
            right: 20,
          },
        },
        scales: {
          y: {
            stacked: true,
            border: {
              display: false,
            },
            beginAtZero: true,
            ticks: {
              maxTicksLimit: 5,
              callback: (value) => formatValue(+value),
              color: textColor.light,
            },
            grid: {
              color: gridColor.light,
            },
          },
          x: {
            stacked: true,
            type: 'time',
            time: {
              parser: 'MM-DD-YYYY',
              unit: 'month',
              displayFormats: {
                month: 'MMM YY',
              },
            },
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            ticks: {
              autoSkipPadding: 48,
              maxRotation: 0,
              color: textColor.light,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: () => '', // Disable tooltip title
              label: (context) => formatValue(context.parsed.y),
            },
            bodyColor: tooltipBodyColor.light,
            backgroundColor: tooltipBgColor.light,
            borderColor: tooltipBorderColor.light,
          },
        },
        interaction: {
          intersect: false,
          mode: 'nearest',
        },
        animation: {
          duration: 200,
        },
        maintainAspectRatio: false,
        resizeDelay: 200,
      },
    });
    setChart(newChart);
    return () => newChart.destroy();
  }, [
    data,
    gridColor.light,
    textColor.light,
    tooltipBgColor.light,
    tooltipBodyColor.light,
    tooltipBorderColor.light,
  ]);

  useEffect(() => {
    if (!chart) return;
    chart.options.scales!.x!.ticks!.color = textColor.light;
    chart.options.scales!.y!.ticks!.color = textColor.light;
    chart.options.scales!.y!.grid!.color = gridColor.light;
    chart.options.plugins!.tooltip!.bodyColor = tooltipBodyColor.light;
    chart.options.plugins!.tooltip!.backgroundColor = tooltipBgColor.light;
    chart.options.plugins!.tooltip!.borderColor = tooltipBorderColor.light;

    chart.update('none');
  }, [
    chart,
    gridColor.light,
    textColor.light,
    tooltipBgColor.light,
    tooltipBodyColor.light,
    tooltipBorderColor.light,
  ]);

  return <canvas ref={canvas} width={width} height={height}></canvas>;
};

type PropsType = {
  data: ChartData;
  width: number;
  height: number;
};

export default BarChart;
